import React, { useState, useEffect, useRef, useCallback } from "react";
import { Modal } from "newui/components/ui/Modal";
import { InputWithLabel } from "newui/components/ui/InputWithLabel";
import { SectorSelect } from "newui/components/ui/CommonSelects/SectorSelect";
import { LocationPicker } from "newui/components/ui/LocationPicker";
import { InstructionsInfoBox } from "newui/components/ui/InstructionsInfoBox/InstructionsInfoBox";
import { usePastProjectSearch } from "../hooks/usePastProjectSearch";
import type {
	KeyPersonnelProject,
	PastProjectSearchResult,
} from "../types/KeyPersonnel.type";
import type { SelectOption } from "newui/components/ui/InputWithLabel/SelectWithLabel";


interface ProjectDetailsModalProps {
	currentPosition?: string;
	editProject?: KeyPersonnelProject;
	excludeProjectIds?: number[];
	isOpen: boolean;
	onClose: () => void;
	onSave: (project: KeyPersonnelProject) => void;
}

const emptyProject: KeyPersonnelProject = {
	name: "",
	sector_id: null,
	sector_ids: [],
	role: "",
	location: null,
	value: "",
	value_humanized: "",
	images: [],
	sector: null,
	sectors: [],
	isCompanyProject: false,
	project_id: null,
};

export const ProjectDetailsModal: React.FC<ProjectDetailsModalProps> = ({
	currentPosition,
	isOpen,
	onClose,
	onSave,
	editProject,
	excludeProjectIds = [],
}) => {
	const [draft, setDraft] = useState<KeyPersonnelProject>({ ...emptyProject, role: currentPosition || "" });
	const [searchTerm, setSearchTerm] = useState("");
	const [showDropdown, setShowDropdown] = useState(false);
	const [isFromSearch, setIsFromSearch] = useState(false);
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [submitted, setSubmitted] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);

	const { data: searchData, isLoading: isSearching } = usePastProjectSearch(
		searchTerm,
		isOpen && !isFromSearch
	);

	const validate = useCallback((d: KeyPersonnelProject): Record<string, string> => {
		const errs: Record<string, string> = {};
		if (!d.name.trim()) errs.name = "Project name is required";
		if (!d.role.trim()) errs.role = "Role is required";
		if (!d.isCompanyProject) {
			if (!d.sector_ids?.length) errs.sector = "At least one sector is required";
			if (!d.location?.trim()) errs.location = "Location is required";
			if (!d.value.trim()) errs.value = "Value is required";
		}
		return errs;
	}, []);

	useEffect(() => {
		if (isOpen) {
			if (editProject) {
				setDraft(editProject);
				setSearchTerm(editProject.name);
				setIsFromSearch(editProject.isCompanyProject);
			} else {
				setDraft({ ...emptyProject, role: currentPosition || "" });
				setSearchTerm("");
				setIsFromSearch(false);
			}
			setShowDropdown(false);
			setErrors({});
			setSubmitted(false);
		}
	}, [isOpen, editProject, currentPosition]);

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
				setShowDropdown(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const val = e.target.value;
		setSearchTerm(val);
		setDraft((prev) => ({
			...prev,
			name: val,
			isCompanyProject: false,
			project_id: null,
			sector: null,
			sector_id: null,
			sectors: [],
			sector_ids: [],
			location: null,
			value: "",
			value_humanized: "",
		}));
		setIsFromSearch(false);
		setShowDropdown(val.length >= 2);
	};

	const handleSelectProject = (project: PastProjectSearchResult) => {
		const projectSectors = project.sectors ?? (project.sector ? [project.sector] : []);
		setDraft((prev) => ({
			...prev,
			name: project.name,
			sector: project.sector,
			sector_id: project.sector?.id ?? null,
			sectors: projectSectors,
			sector_ids: projectSectors.map((s) => s.id),
			location: project.location,
			value: project.value != null ? String(project.value) : "",
			value_humanized: project.value_humanized,
			isCompanyProject: true,
			project_id: project.id,
		}));
		setSearchTerm(project.name);
		setIsFromSearch(true);
		setShowDropdown(false);
	};

	const handleSectorChange = (selected: SelectOption | SelectOption[] | null) => {
		if (!selected) {
			setDraft((prev) => ({
				...prev,
				sector: null,
				sector_id: null,
				sectors: [],
				sector_ids: [],
			}));
			return;
		}
		const items = Array.isArray(selected) ? selected : [selected];
		const sectors = items.map((s) => ({ id: Number(s.value), name: s.label }));
		setDraft((prev) => ({
			...prev,
			sector: sectors[0] ?? null,
			sector_id: sectors[0]?.id ?? null,
			sectors,
			sector_ids: sectors.map((s) => s.id),
		}));
	};

	const handleLocationChange = (location: string) => {
		setDraft((prev) => ({
			...prev,
			location: location || null,
		}));
	};

	// Re-validate on draft change after first submit attempt
	useEffect(() => {
		if (submitted) {
			setErrors(validate(draft));
		}
	}, [draft, submitted, validate]);

	const handleSave = useCallback(() => {
		setSubmitted(true);
		const errs = validate(draft);
		setErrors(errs);
		if (Object.keys(errs).length > 0) return;
		onSave(draft);
		onClose();
	}, [draft, onSave, onClose, validate]);

	const results = (searchData?.past_projects ?? []).filter(
		(p) => !excludeProjectIds.includes(p.id)
	);

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={editProject ? "Edit Project" : "Add Project"}
			iconType={editProject ? "edit" : "create"}
			size="xl"
			closeOnEsc
			primaryButton={{
				text: "Save",
				onClick: handleSave,
			}}
			secondaryButton={{
				text: "Cancel",
				onClick: onClose,
			}}
		>
			<div className="flex flex-col gap-4">
				<InstructionsInfoBox>
					Search for a past project to auto-fill details, or type a project name
					manually if no match is found.
				</InstructionsInfoBox>

				{/* Project Name with search dropdown */}
				<div className="relative" ref={dropdownRef}>
					<InputWithLabel
						label="Project Name"
						value={searchTerm}
						onChange={handleSearchChange}
						placeholder="Search for a project or type a name..."
						testId="project-search-input"
						required
						hasError={!!errors.name}
						errorText={errors.name}
					/>
					{showDropdown && searchTerm.length >= 2 && (
						<div
							className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded border border-gray-200 bg-white shadow-lg"
							data-testid="project-search-dropdown"
						>
							{isSearching ? (
								<div className="px-4 py-3 text-sm text-gray-400">
									Searching...
								</div>
							) : results.length === 0 ? (
								<div className="px-4 py-3 text-sm text-gray-400">
									No projects found
								</div>
							) : (
								results.map((project) => (
									<button
										key={project.id}
										type="button"
										onClick={() => handleSelectProject(project)}
										className="flex w-full flex-col px-4 py-2 text-left hover:bg-blue-50"
										data-testid={`search-result-${project.id}`}
									>
										<span className="text-sm font-medium text-gray-800">
											{project.name}
										</span>
										<span className="text-xs text-gray-500">
											{[
												project.sectors?.length
													? project.sectors.map((s) => s.name).join(", ")
													: project.sector?.name,
												project.location,
												project.value_humanized,
											]
												.filter(Boolean)
												.join(" | ")}
										</span>
									</button>
								))
							)}
						</div>
					)}
				</div>

				<InputWithLabel
					label="Your Role on the Project"
					value={draft.role}
					onChange={(e) =>
						setDraft((prev) => ({ ...prev, role: e.target.value }))
					}
					placeholder="Enter role on this project"
					testId="project-role-input"
					required
					hasError={!!errors.role}
					errorText={errors.role}
				/>

				{isFromSearch ? (
					<InputWithLabel
						label="Sectors"
						value={draft.sectors?.map((s) => s.name).join(", ") ?? ""}
						disabled
						testId="project-sector-input"
					/>
				) : (
					<SectorSelect
						label="Sectors"
						selectedIds={draft.sector_ids ?? []}
						onChange={handleSectorChange}
						placeholder="Select sectors"
						testId="project-sector-select"
						isMulti
						required
						maxSelect={5}
						hasError={!!errors.sector}
						errorText={errors.sector}
					/>
				)}

				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">

					{isFromSearch ? (
						<InputWithLabel
							label="Project Location"
							value={draft.location ?? ""}
							disabled
							testId="project-location-input"
						/>
					) : (
						<LocationPicker
							label="Project Location"
							value={draft.location ?? ""}
							onChange={handleLocationChange}
							placeholder="Search address"
							testId="project-location-picker"
							required
							hasError={!!errors.location}
							errorText={errors.location}
						/>
					)}

					<InputWithLabel
						label="Project Construction Value (AUD)"
						value={isFromSearch ? draft.value_humanized : draft.value}
						disabled={isFromSearch}
						onChange={(e) =>
							setDraft((prev) => ({
								...prev,
								value: e.target.value,
								value_humanized: e.target.value,
							}))
						}
						placeholder="Value"
						testId="project-value-input"
						required={!isFromSearch}
						hasError={!!errors.value}
						errorText={errors.value}
					/>
				</div>
			</div>
		</Modal>
	);
};
